import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame, TrendingUp, Gauge, Sparkles } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";

import { api } from "../lib/api";
import Badges from "../components/Badges";

const Stat = ({ icon: Icon, label, value, suffix }) => (
    <div className="rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-5">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400">
            <Icon size={12} strokeWidth={1.5} />
            {label}
        </div>
        <div className="font-serif text-5xl sm:text-6xl font-light tracking-tight mt-2">
            {value ?? "—"}
            {suffix && value != null ? <span className="text-2xl text-stone-400 ml-1">{suffix}</span> : null}
        </div>
    </div>
);

export default function Insights() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/insights").then(({ data }) => setData(data));
    }, []);

    if (!data) {
        return <div className="min-h-[60vh] flex items-center justify-center text-stone-500 italic font-serif text-2xl">Crunching your reps…</div>;
    }

    return (
        <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-8" data-testid="insights-page">
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-400">The shape of your speech</div>
                <h1 className="font-serif text-4xl sm:text-5xl tracking-tight font-light mt-2">Insights</h1>
            </motion.div>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat icon={Sparkles} label="Sessions" value={data.total_sessions} />
                <Stat icon={Flame} label="Streak" value={data.streak_days} suffix="d" />
                <Stat icon={TrendingUp} label="Avg score" value={data.avg_score} />
                <Stat icon={Gauge} label="Avg WPM" value={data.avg_wpm} />
            </div>

            {/* Badges */}
            {data.badges?.length > 0 && (
                <div className="mt-6">
                    <Badges badges={data.badges} />
                </div>
            )}

            <div className="mt-6 rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-6" data-testid="insights-trend">
                <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400">Score trend</div>
                <div className="mt-4 h-56">
                    {data.trend?.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data.trend} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8A8784" }} tickLine={false} axisLine={false} />
                                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#8A8784" }} tickLine={false} axisLine={false} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 12, border: "1px solid #E8E6E1", fontSize: 12 }}
                                    labelStyle={{ color: "#8A8784" }}
                                />
                                <Line type="monotone" dataKey="score" stroke="#E06D53" strokeWidth={2.5} dot={{ r: 3, fill: "#E06D53" }} activeDot={{ r: 5 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex items-center justify-center text-stone-400 italic font-serif text-lg">
                            Not enough sessions yet. Run a few reps.
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-3">
                <div className="rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-6" data-testid="insights-fillers">
                    <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400">Most-used filler words</div>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {data.top_fillers?.length === 0 && <span className="text-sm text-stone-500 italic">Clean speech so far. Beautiful.</span>}
                        {data.top_fillers?.map(({ word, count }) => (
                            <span key={word} className="px-3 py-1.5 rounded-full text-sm bg-terracotta/10 text-terracotta border border-terracotta/20">
                                "{word}" × {count}
                            </span>
                        ))}
                    </div>
                </div>
                <div className="rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-6" data-testid="insights-categories">
                    <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400">Category breakdown</div>
                    <div className="mt-4 space-y-2">
                        {data.categories?.length === 0 && <span className="text-sm text-stone-500 italic">No categories yet.</span>}
                        {data.categories?.map(({ category, count }) => (
                            <div key={category} className="flex items-center justify-between text-sm">
                                <span>{category}</span>
                                <span className="font-serif text-xl">{count}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
